import React from "react";
import { Link } from "react-router-dom";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { ReadOnlyField } from "../../../common/u1/ReadOnlyField";
import { SearchIcon } from "../../../../assets/ui/icons";
import { EditDialog } from "./EditDialog";
import { FileDropzone } from "./FileDropzone";
import { useBankForm } from "./useBankForm";

/* Add / edit a bank account — the design's two steps in one dialog. */
export const BankFormModal = ({ editing, onClose, onSaved }) => {
  const {
    iban,
    setIban,
    matched,
    file,
    setFile,
    errors,
    setErrors,
    isLooking,
    busy,
    search,
    reset,
    submit,
  } = useBankForm({ editing, onSaved });

  const support = (
    <p className="m-0 mt-4 text-center text-[12.5px] text-[var(--muted)] font-sans">
      Bank Not Found?{" "}
      <Link
        to="/dashboard/help"
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-[var(--coral)] hover:text-[var(--coral-deep)]"
      >
        Contact Support
      </Link>
    </p>
  );

  /* STEP ONE — the lookup. No dialog CTA: the design puts the only action
     inside the field. Enter still searches, because EditDialog's body is a
     form and this is its submit handler. */
  if (!matched) {
    return (
      <EditDialog title="Add Banking Information" onClose={onClose} onSubmit={search}>
        <FieldLabel htmlFor="u1-bank-iban" variant="field" required>
          IBAN
        </FieldLabel>

        <div className="relative">
          <input
            id="u1-bank-iban"
            value={iban}
            placeholder="Enter IBAN"
            autoComplete="off"
            onChange={(e) => {
              setIban(e.target.value);
              if (errors.iban) setErrors({});
            }}
            aria-invalid={errors.iban ? "true" : undefined}
            aria-describedby={errors.iban ? "u1-bank-iban-error" : "u1-bank-iban-hint"}
            className={cn(
              /* Room for the button pinned inside the field's trailing edge. */
              "h-11 w-full rounded-lg border bg-white pl-3.5 pr-[148px] text-[14px]",
              "uppercase text-[var(--ink)] font-sans placeholder:normal-case",
              "transition-[border-color,box-shadow] duration-150",
              "focus:outline-none focus:border-[var(--orange)]",
              "focus:shadow-[0_0_0_4px_var(--focus-ring)]",
              errors.iban
                ? "border-[var(--coral)]"
                : "border-[var(--field-border)] hover:border-[var(--field-border-hover)]"
            )}
          />

          <Button
            type="button"
            variant="coralSolid"
            size="sm"
            onClick={search}
            disabled={isLooking}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 gap-1.5"
          >
            <SearchIcon size={13} />
            {isLooking ? "Searching…" : "Search Bank"}
          </Button>
        </div>

        {errors.iban ? (
          <p
            id="u1-bank-iban-error"
            role="alert"
            className="m-0 mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans"
          >
            {errors.iban}
          </p>
        ) : (
          <p
            id="u1-bank-iban-hint"
            className="m-0 mt-1.5 text-[12px] text-[var(--muted)] font-sans"
          >
            We&apos;ll match the IBAN to its bank automatically.
          </p>
        )}

        {support}
      </EditDialog>
    );
  }

  /* STEP TWO — what the lookup found, plus the confirmation letter. */
  return (
    <EditDialog
      title="Add Banking Information"
      onClose={onClose}
      ctaLabel="Submit"
      onSubmit={submit}
      submitting={busy}
    >
      {/* Read-only, all three: the lookup is the authority on the bank, and
          the live screen shows them disabled for the same reason. */}
      <ReadOnlyField label="IBAN" value={matched.IBAN} />
      <ReadOnlyField className="mt-3.5" label="Bank Name" value={matched.bank_name} />
      <ReadOnlyField className="mt-3.5" label="Your Country" value={matched.country} />

      {/* Not in the live flow: nothing there let you correct a wrong IBAN
          without closing the dialog and starting again. */}
      {!editing && (
        <button
          type="button"
          onClick={reset}
          className={cn(
            "mt-2 rounded px-1 text-[12px] font-bold text-[var(--coral)] font-sans",
            "transition-colors hover:text-[var(--coral-deep)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
          )}
        >
          Search a different IBAN
        </button>
      )}

      <div className="mt-4">
        <p className="m-0 mb-2 text-[13px] font-bold text-[var(--ink)] font-sans">
          Upload Bank Confirmation Letter
        </p>
        <FileDropzone
          label="Bank confirmation letter"
          files={file ? [file] : []}
          onChange={(next) => setFile(next?.[0] || null)}
        />
      </div>

      {support}
    </EditDialog>
  );
};

export default BankFormModal;
